import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Tooltip,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import {
  getNurturingRowKey,
  PickDateFilterType,
  NurturingPreferenceRow,
} from '../apiService/runLogsApiService';
import NurturingDateFilterControls from './NurturingDateFilterControls';
import { formatDateForDisplay } from 'utils/dateUtils';

export interface PickCandidateConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  refreshLoading: boolean;
  error: string | null;
  rows: NurturingPreferenceRow[];
  headerRow: string[];
  ttl: number | null;
  soldWaitDays: number | null;
  unsoldWaitDays: number | null;
  batchSizeByRowKey: Record<string, number>;
  onUpdateBatchSize: (rowKey: string, value: number) => void;
  onConfirmPick: () => void;
  onRefresh: () => void;
  pickLoading: boolean;
  dateFilterType: Exclude<PickDateFilterType, 'ALL'> | null;
  onDateFilterTypeChange: (filterType: Exclude<PickDateFilterType, 'ALL'> | null) => void;
  startDate: string | null;
  onStartDateChange: (date: string | null) => void;
  endDate: string | null;
  onEndDateChange: (date: string | null) => void;
  isAllChecked: boolean;
  onAllCheckedChange: (checked: boolean) => void;
}

export function PickCandidateConfirmationModal({
  open,
  onClose,
  loading,
  refreshLoading,
  error,
  rows,
  headerRow,
  ttl,
  soldWaitDays,
  unsoldWaitDays,
  batchSizeByRowKey,
  onUpdateBatchSize,
  onConfirmPick,
  onRefresh,
  pickLoading,
  dateFilterType,
  onDateFilterTypeChange,
  startDate,
  onStartDateChange,
  endDate,
  onEndDateChange,
  isAllChecked,
  onAllCheckedChange,
}: PickCandidateConfirmationModalProps) {
  const [inputValues, setInputValues] = React.useState<Record<string, string>>({});
  const todayIsoDate = React.useMemo(() => new Date().toISOString(), []);

  React.useEffect(() => {
    if (!open) return;
    const next: Record<string, string> = {};
    for (const row of rows) {
      const rowKey = getNurturingRowKey(row);
      next[rowKey] = String(batchSizeByRowKey[rowKey] ?? row.batch_size);
    }
    setInputValues(next);
  }, [open, rows, batchSizeByRowKey]);

  const handleBatchSizeChange = (rowKey: string, raw: string) => {
    setInputValues(prev => ({ ...prev, [rowKey]: raw }));
    const parsed = parseInt(raw, 10);
    if (!isNaN(parsed) && parsed >= 0) {
      onUpdateBatchSize(rowKey, parsed);
    }
  };

  const handleBatchSizeBlur = (rowKey: string) => {
    const raw = inputValues[rowKey] ?? '';
    const parsed = parseInt(raw, 10);
    if (isNaN(parsed) || parsed < 0) {
      setInputValues(prev => ({
        ...prev,
        [rowKey]: String(batchSizeByRowKey[rowKey] ?? 0),
      }));
    }
  };

  const headers =
    headerRow.length >= 4
      ? headerRow
      : [
          'Segment',
          'Available for Nurturing Candidate',
          'Maiq Lead Type',
          'Batch Size',
        ];
  const showEarliestAvailableDateColumn =
    headers.length >= 5 ||
    rows.some(
      row =>
        Boolean(row.earliest_available_date) ||
        Boolean(row.earliestAvailableForNurturingDate),
    );
  const getEarliestDateLabel = (
    row: NurturingPreferenceRow,
  ): string => {
    const rawDate =
      row.earliest_available_date ??
      row.earliestAvailableForNurturingDate;
    if (!rawDate) return '—';
    return formatDateForDisplay(rawDate, 'dd MMM yyyy') || '—';
  };

  const isSinceDate = dateFilterType === 'SINCE_DATE';
  const isDateRange = dateFilterType === 'DATE_RANGE';
  const isDateSelectionInvalid =
    (isSinceDate && !startDate) || (isDateRange && (!startDate || !endDate));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, maxHeight: '90vh' } }}
      aria-labelledby="pick-candidate-confirmation-modal-title"
    >
      <DialogTitle id="pick-candidate-confirmation-modal-title">
        Pick Candidate Confirmation
      </DialogTitle>

      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Review and adjust the batch sizes below before picking candidates. The edited values
              will be included in the pick request.
            </Typography>

            {/* Read-only meta info — TTL and wait days */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'flex-start',
                gap: 2,
                mb: 2,
                p: 1.5,
                bgcolor: 'grey.50',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                position: 'relative',
                pr: 6,
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  TTL (seconds)
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {ttl ?? '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Sold Wait Days
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {soldWaitDays ?? '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Unsold Wait Days
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {unsoldWaitDays ?? '—'}
                </Typography>
              </Box>

              {/* Refresh icon — top-right of meta box */}
              <Tooltip title="Refresh preferences">
                <span style={{ position: 'absolute', top: 6, right: 6 }}>
                  <IconButton
                    size="small"
                    onClick={onRefresh}
                    disabled={refreshLoading || loading || pickLoading}
                    aria-label="Refresh preferences"
                  >
                    {refreshLoading ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <RefreshIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            <Box
              sx={{
                mb: 2,
                p: 1.5,
                bgcolor: 'grey.50',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Date Filter
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={isAllChecked}
                    onChange={e => onAllCheckedChange(e.target.checked)}
                  />
                }
                label="All"
              />
              <NurturingDateFilterControls
                showAllOption={false}
                dateFilterType={dateFilterType}
                onDateFilterTypeChange={onDateFilterTypeChange}
                startDate={startDate}
                onStartDateChange={onStartDateChange}
                endDate={endDate}
                onEndDateChange={onEndDateChange}
                disabled={pickLoading || loading}
                maxDate={todayIsoDate}
              />
            </Box>

            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, overflowX: 'auto' }}>
              <Table size="small" stickyHeader sx={{ minWidth: 1150 }}>
                <TableHead>
                  <TableRow>
                    {headers.map((h, i) => (
                      <TableCell key={i} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={headers.length}
                        align="center"
                        sx={{ py: 3, color: 'text.secondary' }}
                      >
                        No segments
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map(row => {
                      const rowKey = getNurturingRowKey(row);
                      return (
                        <TableRow key={rowKey}>
                          <TableCell>{row.segment_name}</TableCell>
                          <TableCell>{row.available_for_nurturing_candidate}</TableCell>
                          {showEarliestAvailableDateColumn && (
                            <TableCell>
                              {getEarliestDateLabel(row)}
                            </TableCell>
                          )}
                          <TableCell>{row.maiq_lead_type ?? '—'}</TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              type="number"
                              inputProps={{ min: 0, style: { textAlign: 'right' } }}
                              value={inputValues[rowKey] ?? ''}
                              onChange={e => handleBatchSizeChange(rowKey, e.target.value)}
                              onBlur={() => handleBatchSizeBlur(rowKey)}
                              disabled={pickLoading}
                              sx={{ width: 120 }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'flex-end', px: 3, pb: 2, gap: 1 }}>
        <Button variant="outlined" onClick={onClose} disabled={pickLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={onConfirmPick}
          disabled={pickLoading || loading || isDateSelectionInvalid}
          startIcon={
            pickLoading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <PersonSearchIcon fontSize="small" />
            )
          }
        >
          {pickLoading ? 'Picking...' : 'Confirm & Pick Candidates'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
